<template>
  <v-data-table
    :headers="headers"
    :items="users"
    :options.sync="options"
    :server-items-length="total"
    :footer-props="{
      showFirstLastPage: true,
      firstIcon: 'mdi-arrow-collapse-left',
      lastIcon: 'mdi-arrow-collapse-right',
      prevIcon: 'mdi-arrow-left',
      nextIcon: 'mdi-arrow-right',
      itemsPerPageOptions: [10, 25, 50, 100],
    }"
    :loading="loading"
    class="elevation-1"
  >
    <template v-slot:top>
      <v-toolbar
        flat
        color="white"
      >
        <v-toolbar-title>{{ $t("lbl.users") }}</v-toolbar-title>
        <v-divider
          class="mx-4"
          inset
          vertical
        />
        <v-spacer />
        <v-dialog
          v-model="dialog"
          max-width="400px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="auth('manageUsers')"
              color="primary"
              class="mb-2"
              v-bind="attrs"
              v-on="on"
              :disabled="disable"
            >{{
              $t("lbl.newUser")
            }}</v-btn>
          </template>
          <v-form
            ref="form"
            v-model="valid"
          >
            <v-card>
              <v-card-title>
                <span class="headline">{{ formTitle }}</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="editedItem.name"
                        :rules="rules.nameRules"
                        :label="$t('lbl.name')"
                      />
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="editedItem.email"
                        :rules="rules.emailRules"
                        :label="$t('lbl.email')"
                      />
                    </v-col>
                    <v-col cols="12">
                      <template v-if="editedIndex === -1">
                        <v-text-field
                          v-model="editedItem.password"
                          :type="'password'"
                          :rules="rules.passwordRules"
                          :label="$t('lbl.password')"
                        />
                      </template>
                      <template v-else>
                        <v-text-field
                          v-model="editedItem.password"
                          :disabled="locked"
                          :type="'password'"
                          :rules="!locked ? rules.passwordRules : []"
                          :label="$t('lbl.password')"
                        >
                          <v-icon
                            slot="append-outer"
                            @click="locked = !locked"
                            color="red"
                            class="lock-button"
                          >
                            mdi-lock
                          </v-icon>
                        </v-text-field>
                      </template>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-btn
                  color="blue darken-1"
                  text
                  @click="reset"
                >{{ $t("lbl.clear") }}</v-btn>
                <v-spacer />
                <v-btn
                  color="blue darken-1"
                  text
                  @click="dialog = false"
                >{{
                  $t("lbl.cancel")
                }}</v-btn>
                <v-btn
                  color="blue darken-1"
                  text
                  :disabled="!valid"
                  @click="save"
                >{{
                  $t("lbl.save")
                }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
        </v-dialog>
      </v-toolbar>
    </template>
    <template v-slot:[`item.role`]="{ item }">
      <span>{{ getEnumText(roles, item.role) }}</span>
    </template>
    <template v-slot:[`item.actions`]="{ item }">
      <v-icon
        class="mr-2"
        :disabled="disable"
        @click="editItem(item)"
      > mdi-pencil </v-icon>
      <v-icon
        :disabled="disable"
        @click="deleteItem(item)"
      > mdi-delete </v-icon>
    </template>
  </v-data-table>
</template>

<script>
import { mapActions } from "vuex";
import { roles } from "../common/enums";
import {
  required, email, minLength, password,
} from "../common/validation";

export default {
  data: () => ({
    dialog: false,
    disable: false,
    valid: true,
    loading: true,
    options: {},
    total: 0,
    users: [],
    editedIndex: -1,
    editedItem: {
      name: "",
      email: "",
      password: "",
    },
    defaultItem: {
      name: "",
      email: "",
      password: "",
    },
    rules: {
      nameRules: [required, minLength(3)],
      emailRules: [required, email],
      roleRules: [required],
      passwordRules: [required, minLength(8), password],
    },
    locked: true,
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1
        ? this.$t("lbl.newUser")
        : this.$t("lbl.editUser");
    },
    headers() {
      const columns = [
        { text: this.$t("lbl.name"), value: "name" },
        { text: this.$t("lbl.email"), value: "email" },
      ];
      if (this.auth("get")) {
        columns.push(
          { text: this.$t("lbl.role"), value: "role" },
          { text: this.$t("lbl.reseller"), value: "reseller.name" },
          { text: this.$t("lbl.retailer"), value: "retailer.name" },
        );
      }
      if (this.auth("manageUsers")) {
        columns.push({
          text: this.$t("lbl.actions"),
          value: "actions",
          align: "center",
          sortable: false,
        });
      }
      return columns;
    },
    roles() {
      return roles();
    },
  },

  watch: {
    dialog(val) {
      // eslint-disable-next-line no-unused-expressions
      val || this.close();
    },
    options: {
      handler() {
        this.initialize();
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions("user", ["findUsers", "fetchResellerUsers", "getUser", "saveUser", "deleteUser"]),

    getRequester(req) {
      let requester;
      switch (req) {
        case "find":
          if (this.auth("getUsers")) {
            requester = this.findUsers;
          } else if (this.auth("fetchResellerUsers")) {
            requester = this.fetchResellerUsers;
          }
          break;
        default:
          break;
      }
      return requester;
    },

    async initialize() {
      this.loading = true;
      const filters = this.getFilters(this.options);
      try {
        const findUsersReq = this.getRequester("find");
        const { results, totalResults } = await findUsersReq(filters);
        this.users = results;
        this.total = totalResults;
      } catch (error) {
        this.$toasted.error(this.getMessageText(error.message));
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.$refs.form.reset();
    },

    close() {
      this.reset();
      this.locked = true;
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
      });
    },

    editItem(item) {
      this.editedIndex = this.users.indexOf(item);
      this.editedItem = { ...item };
      this.dialog = true;
    },

    deleteItem(item) {
      this.$swal({
        html: this.$t("msg.deleteText", {
          item: `${item.name} - ${item.email}`,
        }),
        icon: "warning",
        showCancelButton: true,
        focusCancel: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("lbl.delete"),
        cancelButtonText: this.$t("lbl.cancel"),
      }).then((result) => {
        if (result.value) {
          this.disable = true;
          this.deleteUser(item.id)
            .then(() => {
              this.initialize();
              this.$toasted.success(
                this.$t("msg.delete", { item: this.$t("lbl.user") }),
              );
            })
            .catch((error) => {
              this.$toasted.error(this.getMessageText(error.message));
            })
            .finally(() => {
              this.disable = false;
            });
        }
      });
    },

    save() {
      this.dialog = false;
      this.disable = true;
      const editedItem = { ...this.editedItem };
      if (this.editedIndex > -1 && this.locked) {
        delete editedItem.password;
      }

      return this.saveUser(editedItem)
        .then(() => {
          this.initialize();
          this.$toasted.success(
            this.$t("msg.save", { item: this.$t("lbl.user") }),
          );
        })
        .catch((error) => {
          this.$toasted.error(this.getMessageText(error.message));
        })
        .finally(() => {
          this.disable = false;
        });
    },
  },
};
</script>

<style scoped>
.lock-button {
  pointer-events: auto;
}
</style>
